/* eslint-disable no-undef */
import React from 'react';

import { iconConstants } from '@/constants';
import Icons from '@/icons';
import { Backdrop, Button, Input } from '@/kaya';
import { uploadMusicStore } from '@/store';

import { buildGetUrlsPayload, buildSongUploadPayload } from '@/helpers/uploadMusic.helper';
import { getSongUploadUrlsAction, uploadSongAction } from '@/actions/song.actions';
import { get } from 'lodash';
import { uploadMusicTypes } from '@/types';

import styles from './uploadDocumentPopup.module.scss';

interface UploadMusicPopupProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  btnText: string;
  handleChange: (id: string) => (val: string | object | boolean) => void;
  onBack?: () => void;
}

const UploadDocumentPopup2 = (props: UploadMusicPopupProps) => {
  const { visible, setVisible, btnText, handleChange, onBack } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  // const [selectedOption, setSelectedOption] = React.useState('');
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);

  const { formvalues } = uploadMusicStore();
  const [hasError, setHasError] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const currentFileIndex = React.useRef<number>(0);

  const hadnleDoc = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const currentCollectionRights = [...formvalues.collectionRights];
    const selectedCollectionRight = currentCollectionRights[currentFileIndex.current];
    currentCollectionRights[currentFileIndex.current] = {
      ...selectedCollectionRight,
      document: file,
    };

    handleChange('collectionRights')(currentCollectionRights);

    // clear the input files
    inputRef.current!.value = '';
  };

  const handleNext = async () => {
    // check errors
    for (let i = 0; i < formvalues.collectionRights.length; i++) {
      const collectionRight = formvalues.collectionRights[i];
      if (
        !collectionRight.role ||
        !collectionRight.firstName ||
        !collectionRight.lastName ||
        !collectionRight.document
      ) {
        setHasError(true);
        return;
      }
    }

    setHasError(false);

    // Step 1: Get presigned URLs
    const urlsPayload = buildGetUrlsPayload(formvalues, true);
    setIsLoading(true);
    const urlsRes = await getSongUploadUrlsAction({ payload: urlsPayload });
    if (!urlsRes.success) {
      setIsLoading(false);
      alert(`Failed to get upload URLs: ${urlsRes.data.message || JSON.stringify(urlsRes.data)}`);
      return;
    }
    const { song_id, signed_urls, keys } = urlsRes.data;

    let uploadSuccess = true;

    // Upload thumbnail if present
    if (formvalues.thumbnail_image) {
      try {
        const thumbRes = await fetch(signed_urls.thumbnail, {
          method: 'PUT',
          headers: {
            'Content-Type': formvalues.thumbnail_image.type,
          },
          body: formvalues.thumbnail_image,
        });
        if (!thumbRes.ok) {
          throw new Error(`Thumbnail upload failed: ${thumbRes.status}`);
        }
      } catch (e) {
        console.error('Thumbnail upload error:', e);
        uploadSuccess = false;
      }
    }

    // Upload audio
    if (formvalues.media) {
      try {
        const audioRes = await fetch(signed_urls.audio, {
          method: 'PUT',
          headers: {
            'Content-Type': formvalues.media.type,
          },
          body: formvalues.media,
        });
        if (!audioRes.ok) {
          throw new Error(`Audio upload failed: ${audioRes.status}`);
        }
      } catch (e) {
        console.error('Audio upload error:', e);
        uploadSuccess = false;
      }
    }

    // Upload documents
    for (let i = 0; i < formvalues.collectionRights.length; i++) {
      const doc = formvalues.collectionRights[i].document;
      if (doc) {
        try {
          const docRes = await fetch(signed_urls.documents[i].url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/pdf',
            },
            body: doc,
          });
          if (!docRes.ok) {
            throw new Error(`Document ${i + 1} upload failed: ${docRes.status}`);
          }
        } catch (e) {
          console.error(`Document ${i + 1} upload error:`, e);
          uploadSuccess = false;
        }
      }
    }

    if (!uploadSuccess) {
      setIsLoading(false);
      alert('One or more file uploads failed. Please try again.');
      return;
    }

    // Step 2: Create song
    const uploadPayload = buildSongUploadPayload(formvalues, song_id, keys, true);
    const uploadRes = await uploadSongAction({ payload: uploadPayload });
    setIsLoading(false);
    if (uploadRes.success) {
      const url = get(uploadRes, 'data.url', '');
      window.open(url, '_self');
    } else {
      alert(`Failed to create song: ${uploadRes.data.message || JSON.stringify(uploadRes.data)}`);
    }
  };

  const ownerDetails = (collectionRight: uploadMusicTypes.collectionRight, index: number) => (
    <div className={styles.ownerDetails}>
      <p className={styles.index}>{index + 1}</p>
      <div className={styles.entry}>
        <Input
          // placeholder="Title"
          width="100%"
          onChange={(val) => {
            const currentCollectionRights = [...formvalues.collectionRights];
            const selectedCollectionRight = currentCollectionRights[index];
            currentCollectionRights[index] = {
              ...selectedCollectionRight,
              role: val,
            };

            handleChange('collectionRights')(currentCollectionRights);
          }}
          // errors={errors}
          // id="title"
          value={collectionRight.role}
        />
      </div>
      <div className={styles.entry}>
        <Input
          // placeholder="Title"
          width="100%"
          onChange={(val) => {
            const currentCollectionRights = [...formvalues.collectionRights];
            const selectedCollectionRight = currentCollectionRights[index];
            currentCollectionRights[index] = {
              ...selectedCollectionRight,
              firstName: val,
            };

            handleChange('collectionRights')(currentCollectionRights);
          }}
          // errors={errors}
          // id="title"
          value={collectionRight.firstName}
        />
      </div>
      <div className={styles.entry}>
        <Input
          // placeholder="Title"
          width="100%"
          onChange={(val) => {
            const currentCollectionRights = [...formvalues.collectionRights];
            const selectedCollectionRight = currentCollectionRights[index];
            currentCollectionRights[index] = {
              ...selectedCollectionRight,
              lastName: val,
            };

            handleChange('collectionRights')(currentCollectionRights);
          }}
          // errors={errors}
          // id="title"
          value={collectionRight.lastName}
        />
      </div>
      <div className={styles.documentWrap}>
        {collectionRight.document ? (
          <div className={styles.selectedDocument}>
            <p>
              {' '}
              {collectionRight.document.name.length > 20
                ? collectionRight.document.name.slice(0, 16) + '...'
                : collectionRight.document.name}{' '}
            </p>
            <Icons
              style={{
                cursor: 'pointer',
                height: 20,
                width: 20,
              }}
              type={iconConstants.ICONS.CLOSE_ICON}
              onClick={(e) => {
                e.stopPropagation();
                const currentCollectionRights = [...formvalues.collectionRights];
                const selectedCollectionRight = currentCollectionRights[index];
                currentCollectionRights[index] = {
                  ...selectedCollectionRight,
                  document: null,
                };

                handleChange('collectionRights')(currentCollectionRights);
              }}
            />
          </div>
        ) : (
          <div
            className={styles.iconUpload}
            onClick={() => {
              currentFileIndex.current = index;
              inputRef.current?.click();
            }}
          >
            <Icons
              type={iconConstants.ICONS.BIG_UPLOAD}
              style={{
                height: 30,
                width: 30,
              }}
            />
          </div>
        )}
      </div>
      {index > 0 && (
        <div
          className={styles.iconRemoveOwner}
          onClick={() => {
            const currentCollectionRights = [...formvalues.collectionRights];
            currentCollectionRights.splice(index, 1);
            handleChange('collectionRights')(currentCollectionRights);
          }}
        >
          <Icons className={styles.iconRemove} type={iconConstants.ICONS.DELETE} />
        </div>
      )}
    </div>
  );

  return visible ? (
    <div>
      <Backdrop visible={visible} setVisible={setVisible} />
      <div className={styles.uploadMusicPopup}>
        <div className={styles.border}>
          {/* {onBack && ( */}
          <div className={styles.header}>
            <button className={styles.backButton} onClick={onBack} type="button">
              <Icons
                type={iconConstants.ICONS.UP_ARROW}
                style={{
                  transform: 'rotate(-90deg)',
                  height: 16,
                  width: 16,
                }}
              />
              Back
            </button>
          </div>
          {/* )} */}
          <div className={styles.content}>
            <div className={styles.notice}>
              <p>
                {`We appreciate your honesty! To proceed, we'll need permissions from all copyright
                holders since you'll be collecting royalties for the playbacks. Please have all
                rights holders fill out and sign this `}
                <a
                  href="/4Play-Collection_rights_agreement.pdf"
                  download="4Play-Collection_rights_agreement.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  Document
                </a>
                {`. Once completed, you'll be
                good to go!`}
              </p>
            </div>
            <div className={styles.owner}>
              <div>
                <p className={styles.title}>Copyright holders:</p>
              </div>
              <div
                className={styles.ownerDetails}
                style={{
                  marginBottom: 10,
                }}
              >
                <div className={styles.entry}>
                  <p className={styles.fieldName}>Role in the work</p>
                </div>
                <div className={styles.entry}>
                  <p className={styles.fieldName}>First Name</p>
                </div>
                <div className={styles.entry}>
                  <p className={styles.fieldName}>Last Name</p>
                </div>
                <div className={styles.documentWrap}>
                  <p className={styles.fieldName}>Document</p>
                </div>
              </div>
              {formvalues.collectionRights.map((collectionRight, index) => (
                <div key={index}>{ownerDetails(collectionRight, index)}</div>
              ))}
              {formvalues.collectionRights.length < 5 && (
                <div
                  className={styles.addOwner}
                  onClick={() => {
                    handleChange('collectionRights')([
                      ...formvalues.collectionRights,
                      {
                        role: '',
                        firstName: '',
                        lastName: '',
                        document: null,
                      },
                    ]);
                  }}
                >
                  <p>Add owner</p>
                  <Icons className={styles.iconAdd} type={iconConstants.ICONS.ADD_ICON} />
                </div>
              )}
            </div>
            {hasError && <p className={styles.errorMessage}>All Fields Are Required</p>}
          </div>
          <div className={styles.footer}>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="agree"
                checked={acceptedTerms}
                onChange={() => setAcceptedTerms(!acceptedTerms)}
              />
              <p>
                I confirm that all the information I’ve provided is accurate and complete. I
                understand that any false claims or copyright infringement may lead to legal
                consequences, and I am fully aware that copyright infringement is a criminal
                offense.
              </p>
            </div>
            <Button
              className={styles.btn}
              type="Primary"
              width="200px"
              onClick={handleNext}
              disabled={isLoading || !acceptedTerms}
              isLoading={isLoading}
            >
              {btnText}
            </Button>
          </div>
        </div>
        <input
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          ref={inputRef}
          onChange={hadnleDoc}
        />
      </div>
    </div>
  ) : null;
};

export default UploadDocumentPopup2;
